import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import AlertField from "../Components/AlertField";
import Paginate from "../Components/Paginate";
import Records from "../Components/Records";
import useAxiosPrivate from "../Hooks/useAxiosPrivate";
import ConfirmationModal from "../Components/ConfirmationModal";

const UserTable = () => {
  const head = [
    "S.N.",
    "Name",
    "Age",
    "Gender",
    "Contact No.",
    "Email",
    "Active",
    "Joined Date",
    "Action",
  ];
  const { state } = useLocation();
  const [alert, setAlert] = useState(state ? state : null);
  const [values, setValues] = useState();
  const axiosPrivate = useAxiosPrivate();
  const [pageCount, setpageCount] = useState(0);
  const [count, setCount] = useState(0);
  const [endValue, setEndValue] = useState(0);
  const [startValue, setStartValue] = useState(1);
  const [limit, setLimit] = useState("10");
  const [filter, setFilter] = useState("");
  const [cancelActive, setCancelActive] = useState(false);

  useEffect(() => {
    let timeoutId = setTimeout(() => {
      setAlert(null);
    }, 5000);

    return () => clearTimeout(timeoutId);
  }, []);

  const showAlert = (message, type) => {
    setAlert({
      message: message,
      type: type,
    });

    setTimeout(() => {
      setAlert(null);
    }, 5000);
  };

  useEffect(() => {
    //gets list with limit 10 and without any query search at first load
    fetchData(limit, filter);
  }, []);

  const fetchData = async (limit, filter) => {
    try {
      const response = await axiosPrivate.get(
        `user/patient/?limit=${limit}&name=${filter}`
      );
      //sets the list of data got from api
      setValues(response?.data.results);

      //gets the total record and divides by 10 to set pagination number
      setpageCount(Math.ceil(response.data.count / 10));
      //sets the total number of record to display in bottom message
      setCount(response?.data.count);
      //sets the start and end value as length of records got
      setStartValue(1);
      setEndValue(response?.data.results.length);
    } catch (err) {
      if (!err?.response) {
        showAlert("No Server Response", "warning");
      } else {
        showAlert("Load Failed", "warning");
      }
    }
  };

  const fetchList = async (url) => {
    const response = await axiosPrivate.get(url);
    return response;
  };

  const handlePageClick = async (data) => {
    let currentPage = data.selected * limit;

    const url = `user/patient/?limit=${limit}&offset=${currentPage}&name=${filter}`;

    //calls the api with limit and currentpage as offset and get response data
    const response = await fetchList(url);

    //sets response into list
    setValues(response?.data.results);
    //sets start and end value
    setStartValue(data.selected * limit + 1);
    setEndValue(data.selected * limit + response?.data.results.length);
    if (response?.data.results.length === 0) {
      setStartValue(0);
      setEndValue(0);
    }
  };

  const handleRecordClick = async (data) => {
    //make sure api does not hit when record value is same
    if (data.target.value !== limit) {
      //calls the api with new limit chosen
      fetchData(data.target.value, filter);
      setLimit(data.target.value);
    }
  };

  const onSearch = async (e) => {
    if (e.key === "Enter") {
      //replaces white space with %20
      const filter = e.target.value.replace(/\s/g, "%20");
      setFilter(filter);
      fetchData(limit, filter);

      //activates the reset filter button
      setCancelActive(true);
    }
  };

  const onCancel = () => {
    //hides the reset filter button
    setCancelActive(false);
    setFilter("");
    fetchData(limit, "");
    // sets the value of search button to null
    const inputState = document.getElementById("filterTable");
    inputState.value = "";
  };

  const onDelete = async (id) => {
    try {
      //hits the DELETE API of user
      await axiosPrivate.delete(`auth/delete/${id}/`);
      showAlert("User Deleted.", "success");
      //reloads the data in table
      fetchData(limit, filter);
    } catch (err) {
      if (!err?.response) {
        showAlert("No Server Response", "warning");
      } else {
        showAlert("Delete Failed", "warning");
      }
    }
    //closes the modal form
    document.getElementById(`confirmationModal${id}-close`).click();
  };

  return (
    <>
      <AlertField alert={alert} />
      <h2 className="text-center py-2">Users</h2>
      <div className="mx-md-5 px-md-5 mx-4">
        <div className="d-flex justify-content-between flex-column flex-md-row">
          <Records handleRecordClick={handleRecordClick} />
          <div className="w-auto">
            <input
              id="filterTable"
              className="mt-2 w-auto"
              type="text"
              placeholder="Name and press Enter"
              onKeyUp={onSearch}
            />
            {cancelActive && (
              <button
                className="ms-2 btn btn-outline-primary"
                onClick={onCancel}
              >
                <i className="fas fa-xmark p-1"></i>
              </button>
            )}
          </div>
        </div>
        <div className="table-responsive">
          <table className="table table-light table-hover table-striped">
            <thead>
              <tr>
                {head.map((head, i) => (
                  <th key={i}>{head}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {values?.length ? (
                values.map((user, i) => (
                  <tr key={user.id}>
                    <td>{i + 1}</td>
                    <td>{user.user_detail.name}</td>
                    <td>{user.age || "-"}</td>
                    <td>{user.gender || "-"}</td>
                    <td>{user.phone_number || "-"}</td>
                    <td>{user.user_detail.email || "-"}</td>
                    {user.user_detail.is_active ? (
                      <td>
                        <i className="fa-solid fa-check text-success"></i>
                      </td>
                    ) : (
                      <td>
                        <i className="fa-solid fa-xmark text-danger"></i>
                      </td>
                    )}
                    <td>
                      {new Date(
                        user.user_detail.date_joined
                      ).toLocaleDateString() || "-"}
                    </td>
                    <td>
                      <ConfirmationModal
                        button="Delete"
                        target={`confirmationModal${user?.user}`}
                        btnClass="btn btn-danger py-0"
                        title="Delete Confirmation"
                        body={`Are you sure you want to delete account of ${user.user_detail.name}?`}
                        close="No"
                        save="Yes"
                        onClick={() => onDelete(user.user)}
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={9} className="text-center">
                    No User
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Paginate
          startValue={startValue}
          endValue={endValue}
          count={count}
          pageCount={pageCount}
          handlePageClick={handlePageClick}
        />
      </div>
    </>
  );
};

export default UserTable;
